import React, { AnchorHTMLAttributes, FunctionComponent } from 'react';
import Tooltip from '../../tooltip/tooltip-component';

export const SocialIconList = ['fb', 'twitter', 'pinterest', 'youtube', 'insta', 'linkedin', 'tiktok'] as const;
type SocialIconsType = (typeof SocialIconList)[number] | string;

type SocialIconProps = {
  name: SocialIconsType;
  href: string;
  width?: number;
  height?: number;
  background?: string;
  foreground?: string;
  svgClassName?: string;
  tooltipClass?: string;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

/**
 * Social Icon component
 */
export const SocialIcon: FunctionComponent<SocialIconProps> = ({
  name,
  width = 40,
  height = 40,
  background,
  foreground = 'white',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  svgClassName = '',
  href = '',
  tooltipClass,
  title,
  ...anchorAttributes
}) => {
  const socialIcon = (
    <a {...anchorAttributes} href={href}>
      <svg width={width} height={height} viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'>
        {getSvg(name, background, foreground)}
      </svg>
    </a>
  );

  return title ? (
    <Tooltip title={title} className={tooltipClass}>
      {socialIcon}
    </Tooltip>
  ) : (
    socialIcon
  );

  function getSvg(name: SocialIconsType, background?: string, foreground?: string) {
    switch (name) {
      case 'fb': {
        return (
          <>
            <path
              d='M19.9 31.8C26.4722 31.8 31.8 26.4722 31.8 19.9C31.8 13.3278 26.4722 8 19.9 8C13.3278 8 8 13.3278 8 19.9C8 26.4722 13.3278 31.8 19.9 31.8Z'
              fill={background ?? '#3B5998'}
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M18.2642 25.6923H20.4315V19.7391H22.0575L22.379 17.8789H20.4315V16.5309C20.4315 16.0964 20.7125 15.6402 21.1141 15.6402H22.2209V13.78H20.8644V13.7883C18.742 13.8654 18.3059 15.0946 18.2681 16.3857H18.2642V17.8789H17.1799V19.7391H18.2642V25.6923Z'
              fill={foreground}
            />
          </>
        );
      }
      case 'twitter': {
        return (
          <>
            <path
              d='M19.9 31.8c6.572 0 11.9-5.328 11.9-11.9S26.472 8 19.9 8 8 13.328 8 19.9s5.328 11.9 11.9 11.9Z'
              fill={background ?? 'black'}
            />
            <path
              d='m12.585 13.019 5.678 7.591-5.714 6.173h1.286l5.003-5.404 4.042 5.404h4.377l-5.998-8.019 5.319-5.745H25.29l-4.607 4.977-3.723-4.977h-4.376Zm1.891.947h2.01l8.88 11.87h-2.011l-8.879-11.87Z'
              fill={foreground}
            />
          </>
        );
      }
      case 'pinterest': {
        return (
          <>
            <path
              d='M19.7474 31.4949C26.2354 31.4949 31.4949 26.2354 31.4949 19.7474C31.4949 13.2595 26.2354 8 19.7474 8C13.2595 8 8 13.2595 8 19.7474C8 26.2354 13.2595 31.4949 19.7474 31.4949Z'
              fill={background ?? '#CC2127'}
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M19.9152 13.7059C16.6715 13.7059 14.0415 16.3386 14.0415 19.5857C14.0415 22.0059 15.5028 24.0845 17.5896 24.9871C17.5746 24.5772 17.5892 24.0884 17.6929 23.6452C17.8068 23.1669 18.4521 20.4298 18.4521 20.4298C18.4521 20.4298 18.2637 20.0525 18.2637 19.4957C18.2637 18.6221 18.7705 17.9682 19.4004 17.9682C19.9368 17.9682 20.196 18.3722 20.196 18.855C20.196 19.3944 19.8522 20.2011 19.6747 20.9484C19.5277 21.5742 19.9895 22.0853 20.6061 22.0853C21.7221 22.0853 22.4738 20.6494 22.4738 18.9473C22.4738 17.6546 21.6049 16.686 20.0217 16.686C18.2342 16.686 17.1189 18.021 17.1189 19.5125C17.1189 20.0264 17.2709 20.3895 17.5086 20.6698C17.6176 20.7988 17.6323 20.8508 17.5925 20.9993C17.5638 21.1072 17.5003 21.3686 17.4731 21.473C17.434 21.6223 17.3125 21.6747 17.1777 21.6198C16.3546 21.2839 15.9703 20.3809 15.9703 19.366C15.9703 17.6894 17.3827 15.6793 20.1835 15.6793C22.4348 15.6793 23.9157 17.3101 23.9157 19.0593C23.9157 21.3754 22.6298 23.104 20.7351 23.104C20.0985 23.104 19.4994 22.7595 19.2953 22.3691C19.2953 22.3691 18.9519 23.7274 18.8803 23.9908C18.7565 24.4391 18.5182 24.887 18.2973 25.2397C18.8107 25.3866 19.3534 25.4655 19.9152 25.4655C23.1589 25.4655 25.7889 22.8328 25.7889 19.5857C25.7889 16.3386 23.1589 13.7059 19.9152 13.7059Z'
              fill={foreground}
            />
          </>
        );
      }
      case 'youtube': {
        return (
          <>
            <path
              d='M19.9 31.8C26.4722 31.8 31.8 26.4722 31.8 19.9C31.8 13.3278 26.4722 8 19.9 8C13.3278 8 8 13.3278 8 19.9C8 26.4722 13.3278 31.8 19.9 31.8Z'
              fill={background ?? '#E52D27'}
            />
            <path fillRule='evenodd' clipRule='evenodd' d='M16.5 14.46V25.68L25.7504 20.07L16.5 14.46Z' fill={foreground} />
          </>
        );
      }
      case 'insta': {
        return (
          <>
            <path
              d='M19.7375 31.2125C26.0749 31.2125 31.2125 26.075 31.2125 19.7375C31.2125 13.4 26.0749 8.2625 19.7375 8.2625C13.4 8.2625 8.26245 13.4 8.26245 19.7375C8.26245 26.075 13.4 31.2125 19.7375 31.2125Z'
              fill={background ?? '#F4306D'}
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M22.0632 14C23.9475 14 25.475 15.5238 25.475 17.4118V22.0632C25.475 23.9475 23.9512 25.475 22.0632 25.475H17.4118C15.5275 25.475 14 23.9512 14 22.0632V17.4118C14 15.5275 15.5238 14 17.4118 14H22.0632ZM21.9917 15.0387H17.4833C16.1351 15.0387 15.0387 16.1332 15.0387 17.4833V21.9917C15.0387 23.3399 16.1332 24.4363 17.4833 24.4363H21.9917C23.3399 24.4363 24.4363 23.3418 24.4363 21.9917V17.4833C24.4363 16.1351 23.3418 15.0387 21.9917 15.0387Z'
              fill={foreground}
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M19.7375 16.8193C21.3492 16.8193 22.6558 18.1258 22.6558 19.7375C22.6558 21.3492 21.3492 22.6557 19.7375 22.6557C18.1259 22.6557 16.8193 21.3492 16.8193 19.7375C16.8193 18.1258 18.1259 16.8193 19.7375 16.8193ZM19.7375 17.858C18.6995 17.858 17.858 18.6995 17.858 19.7375C17.858 20.7755 18.6995 21.617 19.7375 21.617C20.7756 21.617 21.6171 20.7755 21.6171 19.7375C21.6171 18.6995 20.7756 17.858 19.7375 17.858Z'
              fill={foreground}
            />
            <path
              d='M22.7546 17.528C23.228 17.528 23.6117 17.1443 23.6117 16.6709C23.6117 16.1975 23.228 15.8138 22.7546 15.8138C22.2812 15.8138 21.8975 16.1975 21.8975 16.6709C21.8975 17.1443 22.2812 17.528 22.7546 17.528Z'
              fill={foreground}
            />
          </>
        );
      }
      case 'linkedin': {
        return (
          <>
            <path
              d='M19.9 31.8C26.4722 31.8 31.8 26.4722 31.8 19.9C31.8 13.3278 26.4722 8 19.9 8C13.3278 8 8 13.3278 8 19.9C8 26.4722 13.3278 31.8 19.9 31.8Z'
              fill={background ?? '#0976B4'}
            />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M15.2642 16.8287C16.0835 16.8287 16.7476 16.223 16.7476 15.4751C16.7476 14.7265 16.0835 14.12 15.2642 14.12C14.4442 14.12 13.78 14.7265 13.78 15.4751C13.78 16.223 14.4442 16.8287 15.2642 16.8287Z'
              fill={foreground}
            />
            <path fillRule='evenodd' clipRule='evenodd' d='M14.1201 25.5364H16.7818V17.86H14.1201V25.5364Z' fill={foreground} />
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M20.5174 21.267C20.5174 20.3866 20.9339 19.5258 21.9298 19.5258C22.9257 19.5258 23.1705 20.3866 23.1705 21.2459V25.4384H25.8203V21.0745C25.8203 18.0427 24.0281 17.52 22.9257 17.52C21.8241 17.52 21.2166 17.8926 20.5174 18.7954V17.762H17.8601V25.4384H20.5174V21.267Z'
              fill={foreground}
            />
          </>
        );
      }
      case 'tiktok': {
        return (
          <>
            <path
              d='M19.9 31.8c6.572 0 11.9-5.328 11.9-11.9S26.472 8 19.9 8 8 13.328 8 19.9s5.328 11.9 11.9 11.9'
              fill={background ?? '#262626'}
            />
            <path
              d='M16.723 24.674c.015.015.03.025.044.04.13.091.228.227.354.33.394.315.83.485 1.336.514 1.195.073 2.24-.825 2.375-2.006q.006-.087.005-.175v-10.68c0-.238 0-.238.238-.238h1.622c.049 0 .102-.01.15.024.006.005.006.005.01.005a.2.2 0 0 1 .04.097 3.75 3.75 0 0 0 1.505 2.302c.049.034.092.083.136.122l.102.087c.126.073.219.19.33.282q.658.547 1.482.757c.048.015.102.01.14.059l.01.01c.034.033.03.077.03.116v1.885c0 .145-.02.15-.166.15-.461 0-.913-.063-1.36-.165a6.5 6.5 0 0 1-2.132-.918c-.034-.024-.073-.073-.107-.053-.048.019-.024.082-.024.121 0 1.768.005 3.536-.005 5.3-.005 1.233-.398 2.34-1.21 3.283-.859 1-1.952 1.578-3.258 1.738a5 5 0 0 1-2.876-.51 2.5 2.5 0 0 1-.418-.257l-.126-.112c-.316-.257-.53-.592-.733-.937a4.9 4.9 0 0 1-.617-1.705 4.7 4.7 0 0 1-.068-1.05c.063-1.13.442-2.141 1.17-3.01a5 5 0 0 1 2.278-1.55 4.7 4.7 0 0 1 1.161-.238c.073-.005.146-.024.224-.02.034 0 .073 0 .102.025l.01.01c.019.034.014.072.014.111v1.977c-.005.063-.034.088-.097.068a2.5 2.5 0 0 0-.534-.082 2 2 0 0 0-.816.121c-.875.325-1.409.952-1.516 1.875-.112.908.248 1.637 1.006 2.161.068.049.13.088.189.136'
              fill={foreground}
            />
            <path
              d='M15.076 26.894c.034-.01.054.02.078.034a5.1 5.1 0 0 0 1.84.68c.331.053.666.078 1.001.058 1.122-.058 2.123-.432 2.988-1.146a4.93 4.93 0 0 0 1.665-2.608 5 5 0 0 0 .156-1.01c.01-.195.01-.394.01-.588v-5.168c.043-.01.063.02.087.038a6.55 6.55 0 0 0 3.123 1.122c.165.015.335.015.5.025.073.005.073.005.073-.073v-1.953q-.001-.053.005-.107c.15.015.296.05.447.059.092.005.185.014.282.014.048 0 .068.015.058.063v2.58c0 .072 0 .072-.078.072q-.385-.001-.772-.053a6.4 6.4 0 0 1-2.846-1.093c-.024-.02-.054-.034-.092-.058v5.513c0 .957-.248 1.85-.763 2.661a4.97 4.97 0 0 1-2.565 2.07c-.66.242-1.345.33-2.05.281a4.95 4.95 0 0 1-3.084-1.345.4.4 0 0 1-.063-.068'
              fill='#EC1655'
            />
            <path
              d='M22.844 12.478c-.034 0-.068.005-.107.005H21c-.136 0-.136 0-.136.131v10.623c0 .247-.02.49-.098.723-.271.831-.825 1.375-1.675 1.57-.913.208-1.686-.069-2.302-.778a.1.1 0 0 1-.025-.039c.175.068.35.141.54.18.592.116 1.155.03 1.67-.291.598-.37.947-.904 1.069-1.594a2 2 0 0 0 .029-.393V11.973c0-.165-.024-.146.14-.146h2.512c.082 0 .087 0 .082.088.005.19.005.379.04.563'
              fill='#0CBAC6'
            />
            <path
              d='M18.468 18.297c-.5.014-.991.097-1.462.252a5 5 0 0 0-1.827 1.078 4.9 4.9 0 0 0-1.432 2.337 5 5 0 0 0-.17.995 4.9 4.9 0 0 0 .35 2.234 5.1 5.1 0 0 0 1.005 1.565.1.1 0 0 1 .02.029c-.098-.044-.18-.117-.263-.18a4.98 4.98 0 0 1-1.919-4.279 5.01 5.01 0 0 1 4.639-4.663q.495-.034.99.024c.044.005.074.015.069.068z'
              fill='#0CBAC6'
            />
            <path
              d='M16.723 24.674a2 2 0 0 1-.535-.374 2.288 2.288 0 0 1 .908-3.842q.643-.194 1.293-.02c.024.006.048.015.073.01.014-.034.004-.067.004-.106v-1.933c0-.034.005-.069.005-.107a4 4 0 0 1 .743.034c.04.005.049.024.049.058v2.618c0 .03.01.063-.01.097-.111-.02-.223-.053-.335-.073a2.26 2.26 0 0 0-1.773.471c-.456.36-.743.836-.835 1.414a2.23 2.23 0 0 0 .379 1.69c.01.02.02.044.034.063m7.815-9.665a3 3 0 0 1-.666-.54 3.8 3.8 0 0 1-1.02-1.962v-.02c.233-.014.462 0 .695-.004.039 0 .058.014.053.058v.049c-.01.65.18 1.243.496 1.797.111.199.247.383.393.558.024.02.044.034.049.063'
              fill='#EC1655'
            />
            <path
              d='M26.593 16.194a3.8 3.8 0 0 1-1.744-.884c-.073-.069-.15-.132-.209-.214.024-.005.044.014.063.024q.598.342 1.283.452c.174.029.354.048.534.048.073 0 .078 0 .078.073 0 .165-.005.33-.005.5'
              fill='#0CBAC6'
            />
          </>
        );
      }
    }
  }
};

export default SocialIcon;
